import { FC } from "react";
import { invoiceTotal } from "@1-now/booking-core";
import { dollars } from "./dollars";
import CarPhoto from "./CarPhoto";
import FollowScroll from "./FollowScroll";
import { PriceDetailsToggle, PriceTotal } from "./PriceLines";
import { MAIN_HOVER, NO_TAP_SELECT, fadeUnlessBusy, useClearSelectionWhen } from "./noTapSelect";
import type { PhoneBookingProps } from "./PhoneBooking";
import type { Stop } from "./tripStops";

/** "Pickup and return": where and when the trip starts and ends, on the same rail as the review. */
const TripBox: FC<{ pickup: Stop; dropoff: Stop; onEdit?: () => void }> = ({ pickup, dropoff, onEdit }) => (
  <div className="mt-4 rounded-2xl bg-secondary-100/40 p-4 text-sm">
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-sm font-extrabold">Pickup and return</h3>
      {onEdit && (
        <button type="button" onClick={onEdit} aria-label="Edit pickup and return" className="text-xs font-bold underline">
          Edit
        </button>
      )}
    </div>
    <div className="mt-2 flex gap-3">
      <div className="flex flex-col items-center py-1" aria-hidden="true">
        <span className="h-2.5 w-2.5 rounded-sm bg-text-color" />
        <span className="my-1 w-0.5 flex-1 bg-text-color" />
        <span className="h-2.5 w-2.5 rounded-sm bg-text-color" />
      </div>
      <div className="min-w-0 space-y-2">
        <div>
          <b className="block break-words">{pickup.place}</b>
          <span className="block text-xs opacity-80">{pickup.when}</span>
        </div>
        <div>
          <b className="block break-words">{dropoff.place}</b>
          <span className="block text-xs opacity-80">{dropoff.when}</span>
        </div>
      </div>
    </div>
  </div>
);

/** The spinner a busy button shows instead of its words. */
const Working = (
  <span
    role="status"
    aria-label="Working"
    className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"
  />
);

/** A button beside the main one (the review's "Pay later"). */
export interface SecondaryButton {
  label: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
}

/**
 * The sticky "Booking overview" card on the right of the desktop booking (1NOW-11173), on the car page and on
 * every step: the car, where and when, a "Price details" row that opens the lines in place, the total, and the
 * main button. Without a quote yet it says the day price instead.
 */
const BookingOverview: FC<{
  /** Drawn on the steps; the car page is the car, so it leaves them out. */
  car?: { name?: string; photo?: string | null };
  trip?: { pickup: Stop; dropoff: Stop };
  onEditTrip?: () => void;
  /** The bill the lines and the total are read from. */
  invoice: any;
  /** There is a quote for the chosen trip to show; otherwise the day price is. */
  quoted: boolean;
  dayPrice?: number;
  carName?: string;
  label: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
  /** Under the main button, in the outline style (the review's "Pay later"). */
  secondary?: SecondaryButton;
}> = ({ car, trip, onEditTrip, invoice, quoted, dayPrice, carName, label, onClick, busy, disabled, secondary }) => {
  useClearSelectionWhen(busy || secondary?.busy);
  return (
    <aside
      aria-label="Booking overview"
      data-testid="booking-overview"
      // Nothing in the card scrolls: the lines are one closed row, and an open list makes the card taller.
      // contain:inline-size: the card takes the width of its column and never asks for more. A price line is one
      // line (truncated), so without it a long car name's line made the car page's column as wide as the name.
      className={`min-w-0 rounded-3xl border border-secondary-100 bg-card-color p-5 text-text-color [contain:inline-size] ${NO_TAP_SELECT}`}
    >
      <h2 className="text-sm font-extrabold uppercase tracking-wide">Booking overview</h2>
      {car && (
        <div className="mt-4 flex items-center gap-3">
          <CarPhoto photo={car.photo} className="h-14 w-20 rounded-xl" />
          <b className="line-clamp-2 min-w-0 break-words" title={car.name}>
            {car.name}
          </b>
        </div>
      )}
      {trip && <TripBox pickup={trip.pickup} dropoff={trip.dropoff} onEdit={onEditTrip} />}
      <div className="mt-4">
        {quoted ? (
          <PriceDetailsToggle invoice={invoice} carName={carName} emptyText="Updating the price…" />
        ) : dayPrice ? (
          <div>
            <span className="block text-2xl font-extrabold tabular-nums">
              {dollars(dayPrice)}
              <small className="text-sm font-semibold"> / day</small>
            </span>
            <span className="block text-sm opacity-80">Add your trip for the total</span>
          </div>
        ) : null}
      </div>
      {quoted && <PriceTotal invoice={invoice} carName={carName} />}
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || busy}
        className={`mt-5 inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary px-6 py-3 text-sm font-extrabold text-on-button transition-opacity ${MAIN_HOVER} ${fadeUnlessBusy(busy)}`}
      >
        {busy ? Working : label}
      </button>
      {secondary && (
        <button
          type="button"
          onClick={secondary.onClick}
          disabled={secondary.disabled || secondary.busy}
          className={`mt-2 inline-flex min-h-[48px] w-full items-center justify-center rounded-full border-2 border-text-color/15 px-6 py-3 text-sm font-extrabold text-text-color transition-opacity hover:bg-text-color/5 ${fadeUnlessBusy(secondary.busy)}`}
        >
          {secondary.busy ? Working : secondary.label}
        </button>
      )}
    </aside>
  );
};

export default BookingOverview;

/** The car page's card: the total (or the day price) and "Book this car", which runs the trip check and opens the steps. */
export const CarPageOverview: FC<PhoneBookingProps> = (p) => {
  const quoted = p.ready && (invoiceTotal(p.invoice) !== null || Boolean(p.invoice?.error));
  return (
    <FollowScroll bounds="main">
      <BookingOverview
        trip={p.ready ? p.trip : undefined}
        invoice={p.invoice}
        quoted={quoted}
        dayPrice={Number(p.car?.price) > 0 ? Number(p.car.price) : undefined}
        carName={p.car?.name}
        label="Book this car"
        onClick={p.onBookThisCar}
      />
    </FollowScroll>
  );
};
